<template>
  <ElDialog
    name="manage-integration-modal"
    class="modal-manage-integration"
    :visible="show"
    v-on="$listeners"
    @onClose="onCloseModal"
    append-to-body
  >
    <!-- Title -->
    <template v-slot:title>
      <div class="modal-header">
        <p>{{ modalTitle }}</p>
        <VIntegrationStatus :integration="integration" />
      </div>
    </template>

    <VMessage
      v-if="showAuthSuccess"
      type="success"
      :fullwidth="true">
      <template v-slot:icon>
        <CheckIcon />
      </template>
      <template v-slot:text>
        {{ $t('companySettings.integrations.authorize.success') }}
      </template>
    </VMessage>

    <!-- Body -->
    <template v-if="requiresAuthorisation">
      <p>{{ $t(`companySettings.integrations.${integration.slug}.description`) }}</p>
      <p>{{ $t('companySettings.integrations.authorize.message', {'integration': integrationName}) }}</p>
      <hr>
    </template>
    <template v-else>
      <p>{{ $t(`companySettings.integrations.${integration.slug}.description`) }}</p>
      <hr>
      <slot></slot>
    </template>

    <!-- Logs -->
    <template v-if="showProblems">
      <p><strong>{{ $t(`companySettings.integrations.problems`) }}</strong></p>
      <FeedItem
        v-for="(item, index) in integration.instance.logs"
        :key="index"
        :item="item"
        :historyInUTC="false"
        :showGrayLine="index !== integration.instance.logs.length - 1"
        :itemType="$t('companySettings.integrations.errorLogs')"
      />
      <slot name="log-resolve-message">
        <small>{{ $t('companySettings.integrations.resolveProblemMessage') }}</small>
      </slot>
      <hr>
    </template>

    <!-- CTA -->
    <template v-if="requiresAuthorisation">
      <div class="flex justify-end">
        <BaseButton
          variant="invisible"
          @click.native="$emit('close')"
          size="small"
        >
          {{ $t('functional.cancel') }}
        </BaseButton>
        <ButtonLink
          :to="integration.authorization_url"
          size="small"
          :data-ec="`manage_integration_modal_${integrationName}`"
          data-et="authorize_button"
        >
          {{ $t('companySettings.integrations.authorize.authorize') }}
        </ButtonLink>
      </div>
    </template>
    <template v-else>
      <slot name="cta" ></slot>
    </template>

  </ElDialog>
</template>

<script>
import VIntegrationStatus from '@/views/components/IntegrationStatus.vue';
import ButtonLink from '@/components/library/links/ButtonLink.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import integrationsManagementMixin from '@/mixins/integrationsManagementMixin';
import { CheckIcon } from '@/components/icons/fontIcons';
import FeedItem from '@/components/library/feeds/FeedItem/FeedItem.vue';

export default {
  name: 'ManageIntegrationBase',
  description: 'Shared functionality across integration management components',
  components: {
    BaseButton,
    ButtonLink,
    VMessage,
    VIntegrationStatus,
    CheckIcon,
    FeedItem,
  },
  props: {
    show: Boolean,
    title: {
      type: [String],
      required: true,
    },
    showAuthSuccess: {
      type: [Boolean],
      required: true,
    },
    integration: {
      type: [Object],
      required: true,
    },
  },
  mixins: [
    integrationsManagementMixin,
  ],
  computed: {
    requiresAuthorisation() {
      if (this.hasInstance) {
        return this.integration.instance.status === 'unauthorized';
      }
      return false;
    },
    modalTitle() {
      if (this.requiresAuthorisation) {
        return this.$t('companySettings.integrations.authorizationTitle', { integration: this.integrationName });
      }

      return this.$t('companySettings.integrations.configurationTitle', { integration: this.integrationName });
    },
    integrationName() {
      return this.$t(`companySettings.integrations.${this.integration.slug}.name`);
    },
    showProblems() {
      return this.integration.instance?.logs?.length > 0 && this.integration.instance.status === 'unconfigured';
    },
  },
  methods: {
    onOpenModal() {
      this.errors.clear();
    },
    onCloseModal() {
      this.errors.clear();
    },
    modalClose() {
      this.$emit('close');
    },
    confirmDisconnection() {
      this.$emit('close');

      this.$confirm(this.$t('companySettings.integrations.disconnect.warning', { name: this.integrationName }), this.$t('functional.warning'), {
        confirmButtonClass: 'el-button--medium el-button--danger',
        cancelButtonClass: 'el-button--medium el-button--outlinePrimary',
        confirmButtonText: this.$t('companySettings.integrations.disconnect.disconnect'),
        cancelButtonText: this.$t('companySettings.integrations.disconnect.cancel'),
        type: 'warning',
      }).then(() => {
        this.disconnect();
      }).catch(() => {
        // User has cancelled, do nothing
      });
    },
    disconnect() {
      this.$emit('loadingStateChanged', true);

      const { slug } = this.integration;

      this.$store.dispatch('integrations/DISCONNECT_INTEGRATION_INSTANCE', { slug }).finally(() => {
        this.$emit('loadingStateChanged', false);
      });
    },
  },
};
</script>

<style scoped>
.modal-header {
  display: flex;
  justify-content: space-between;
}

.modal-header p {
  margin-bottom: 0;
}

a.a-button {
  float: none;
  display: inline-block;
}

a.a-button .authlayout {
  position: absolute;
  left: var(--spacing-base);
  bottom: var(--spacing-base);
}

.a-message {
  margin-bottom: var(--spacing-base);
}
</style>
